import type { NgZone } from '@angular/core';
import type { Observable } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { elementScrollUtil } from './element-scroll.util';

/**
 * Notes viewport / focus helpers.
 * — Issue 2 / TR-90903 (Scanner touch, after send): keep `#notes-section` visible + composer focused.
 * — Issue 3 / TR-90903 (nested list): scroll note card into view, then focus editor without extra scroll.
 */

export const NOTES_SECTION_ELEMENT_ID = 'notes-section';

export const RX_TEXTAREA_INPUT_SELECTOR = 'textarea.rx-textarea-input';

/** Issue 2: align notes panel with its scroll parent (`elementScrollUtil`). */
export function scrollNotesSectionIntoNearestScrollParent(doc: Document): void {
	const section = doc.getElementById(NOTES_SECTION_ELEMENT_ID);
	if (!section) {
		return;
	}
	elementScrollUtil.scrollElementToTopInNearestScrollableAncestor(section);
}

/** Issue 2: after stable DOM, scroll→focus composer→scroll; Scanner repeats once after 120ms. */
export function stabilizeNotesSectionAfterSend(
	doc: Document,
	ngZone: NgZone,
	destroy$: Observable<unknown>,
	focusComposer: () => void,
	isScanner: boolean
): void {
	const alignSectionAndRefocusComposer = (): void => {
		scrollNotesSectionIntoNearestScrollParent(doc);
		focusComposer();
		scrollNotesSectionIntoNearestScrollParent(doc);
	};

	queueMicrotask(() => {
		ngZone.onStable.pipe(take(1), takeUntil(destroy$)).subscribe(() => {
			alignSectionAndRefocusComposer();
			if (isScanner) {
				globalThis.setTimeout(alignSectionAndRefocusComposer, 120);
			}
		});
	});
}

export function focusHTMLElementPreferNoScroll(element: HTMLElement): void {
	try {
		element.focus({ preventScroll: true });
	} catch {
		element.focus();
	}
}

/** Issue 3 / TR-90903: `scrollIntoView` on host, then `focus({ preventScroll })` after layout (chained `rAF`). */
export function scrollHostNearestThenFocusDescendantTextarea(
	host: HTMLElement,
	textareaSelector: string,
	onDone?: () => void
): void {
	requestAnimationFrame(() => {
		host.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
		requestAnimationFrame(() => {
			let textarea = host.querySelector(textareaSelector) as HTMLTextAreaElement | null;
			if (textarea) {
				focusHTMLElementPreferNoScroll(textarea);
				onDone?.();
				return;
			}
			requestAnimationFrame(() => {
				textarea = host.querySelector(textareaSelector) as HTMLTextAreaElement | null;
				if (textarea) {
					focusHTMLElementPreferNoScroll(textarea);
				}
				onDone?.();
			});
		});
	});
}

/** Namespace for tests (`spyOn(notesViewportFocusUtil, …)`). */
export const notesViewportFocusUtil = {
	NOTES_SECTION_ELEMENT_ID,
	RX_TEXTAREA_INPUT_SELECTOR,
	focusHTMLElementPreferNoScroll,
	scrollHostNearestThenFocusDescendantTextarea,
	scrollNotesSectionIntoNearestScrollParent,
	stabilizeNotesSectionAfterSend
};
